import { useEffect, useState } from "react";

import ConfirmationModal from "../../../components/composed/ConfirmationModal";
import { DISQUALIFICATION_ACKNOWLEDGEMENT } from "./CreateCustomer.constants";
import { DisqualificationReason } from "../interface";

interface IProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  reasons: DisqualificationReason[];
}

/**
 * Rendered inside the Create Lead sidebar (not portalled to the body like
 * ConfirmationDialog), so it overlays the form instead of sitting behind the
 * sidebar panel. The parent container must be `relative`.
 */
export default function DisqualificationAcknowledgeModal({
  open,
  onCancel,
  onConfirm,
  reasons
}: IProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showReasons, setShowReasons] = useState(false);

  // Every time the popup is reopened the user has to acknowledge again.
  useEffect(() => {
    if (open) {
      setAcknowledged(false);
      setShowReasons(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4">
      <div className="flex max-h-[420px] w-full flex-col overflow-y-auto rounded-xl bg-white p-4 shadow-xl">
        <ConfirmationModal
          onCancel={onCancel}
          onConfirm={onConfirm}
          disableConfirm={!acknowledged}
          cancelButtontext="Cancel"
          confirmButtonText={
            DISQUALIFICATION_ACKNOWLEDGEMENT.CONFIRM_BUTTON_TEXT
          }
        >
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              {DISQUALIFICATION_ACKNOWLEDGEMENT.TEXT_BEFORE_LINK}
              <button
                type="button"
                onClick={() => setShowReasons((prevState) => !prevState)}
                className="font-semibold underline underline-offset-2 text-indigo-900 hover:text-indigo-700"
              >
                {DISQUALIFICATION_ACKNOWLEDGEMENT.LINK_TEXT}
              </button>
              {DISQUALIFICATION_ACKNOWLEDGEMENT.TEXT_AFTER_LINK}
            </span>
          </label>

          {showReasons && (
            <div className="mt-3 max-h-52 overflow-y-auto rounded-lg border border-gray-200">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-gray-100 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {DISQUALIFICATION_ACKNOWLEDGEMENT.REASON_CODE_COLUMN}
                    </th>
                    <th className="px-3 py-2 font-medium">
                      {DISQUALIFICATION_ACKNOWLEDGEMENT.STATUS_COLUMN}
                    </th>
                    <th className="px-3 py-2 font-medium">
                      {DISQUALIFICATION_ACKNOWLEDGEMENT.DESCRIPTION_COLUMN}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {reasons?.map((reason, index) => (
                    <tr
                      key={`${reason?.code}-${index}`}
                      className="border-t border-gray-200"
                    >
                      <td className="px-3 py-2 whitespace-nowrap align-top">
                        {reason?.code}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap align-top">
                        {reason?.status || "-"}
                      </td>
                      <td className="px-3 py-2">{reason?.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ConfirmationModal>
      </div>
    </div>
  );
}
